import React, { useState, useRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useMutation } from 'react-query';
import { FaCommentDots, FaRobot, FaTimes, FaPaperclip, FaShieldAlt } from 'react-icons/fa';

// API
import { sendChatPrompt } from './api/admin.chat.api';

// Components
import ChatBubble from '../UI/molecules/ChatBubble';

// --- STYLES (From Reference) ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); }
  50% { transform: scale(1.05); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.5); }
  100% { transform: scale(1); box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); }
`;

// Launcher Button
const LauncherButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background: #2563eb;
  border: none;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: ${(props) => (props.$isOpen ? 0 : 1)};
  pointer-events: ${(props) => (props.$isOpen ? 'none' : 'all')};
  transform: ${(props) => (props.$isOpen ? 'scale(0.8) rotate(90deg)' : 'scale(1) rotate(0deg)')};

  &:hover {
    animation: ${pulse} 2s infinite;
  }

  /* Force icon color */
  & > svg {
    fill: #ffffff;
    color: #ffffff;
  }
`;

// Close Button (Fixed positioning relative to Wrapper)
const CloseButtonArea = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10002;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af; /* Slightly lighter gray for the icon */
  transition: color 0.2s;

  &:hover {
    color: #4b5563; /* Darker gray on hover */
  }
`;

// Top Resize Handle (Invisible)
const ResizeHandleN = styled.div`
  position: absolute;
  top: 0;
  left: 20px; /* Avoid overlap with NW handle */
  right: 0;
  height: 6px;
  cursor: n-resize;
  z-index: 10009; /* Slightly lower than NW */
`;

// Left Resize Handle (Invisible)
const ResizeHandleW = styled.div`
  position: absolute;
  top: 20px; /* Avoid overlap with NW handle */
  bottom: 0;
  left: 0;
  width: 6px;
  cursor: w-resize;
  z-index: 10009;
`;

// Resize Handle (Top-Left, Beautiful Diagonal Grip)
const ResizeHandleNW = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  height: 40px;
  cursor: nw-resize;
  z-index: 10010;
  border-top-left-radius: 24px; /* Match Wrapper border-radius */
  overflow: hidden; /* Ensure grip stays within rounded corner */

  /* Diagonal Grip Lines Area */
  background: linear-gradient(135deg, #2563eb 0%, #2563eb 50%, transparent 50%, transparent 100%);
  opacity: 0.8;
  transition: all 0.2s ease;

  /* The actual lines */
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    width: 14px;
    height: 14px;

    /* Creating diagonal lines using repeating-linear-gradient */
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 2px,
      rgba(255, 255, 255, 0.7) 2px,
      rgba(255, 255, 255, 0.7) 4px
    );
  }

  /* Hover Effect */
  &:hover {
    opacity: 1;
    width: 44px; /* Smooth expand */
    height: 44px;
    box-shadow: 2px 2px 10px rgba(37, 99, 235, 0.3);
  }
`;

// Main Container (Updated for dynamic size)
export const Wrapper = styled.section<{ $open: boolean; $width: number; $height: number }>`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 10001;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Important for resize handle to be clipped if needed, but handle has own radius */

  /* Transition Logic (Only for Opacity/Transform, NOT width/height to avoid resize lag) */
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? '0' : '20px')}) scale(${({ $open }) => ($open ? 1 : 0.9)});
  transition:
    opacity 0.3s cubic-bezier(0.25, 1, 0.5, 1),
    transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

// ... (Existing Styles)

export const Shell = styled.div`
  --chat-h: 100%;
  display: flex;
  flex-direction: column;
  height: var(--chat-h);
  width: 100%;
  min-height: 0;
`;

export const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  font-weight: 700;
  color: #1f2937;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff; /* Ensure header stands out if body is gray */
`;

export const Messages = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa; /* Distinct background for chat zone */
  overscroll-behavior-y: contain;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
`;

export const Row = styled.div<{ $alignEnd?: boolean }>`
  display: flex;
  padding: 8px 0;
  animation: ${fadeIn} 0.2s ease-out;
  ${({ $alignEnd }) =>
    $alignEnd
      ? css`
          justify-content: flex-end;
        `
      : css`
          justify-content: flex-start;
        `}
`;

export const Composer = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 20px;
  background: #fff;
  flex-shrink: 0;
`;

export const InputGrid = styled.div`
  width: 100%;
  position: relative;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding-bottom: 40px; /* Space for buttons */
  background: #fff;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  height: 80px;
  resize: none;
  border: none;
  background: transparent;
  color: #111827;
  padding: 12px 14px;
  font-size: 15px; /* Match font size */
  line-height: 1.5;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: #9ca3af;
  }
  &:disabled {
    background: #f9fafb;
    color: #d1d5db;
  }
`;

export const Buttons = styled.div`
  display: flex;
  gap: 8px;
  position: absolute;
  bottom: 8px;
  right: 8px;
`;

export const Button = styled.button<{ $variant?: 'submit' | 'cancel' }>`
  height: 32px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $variant }) =>
    $variant === 'cancel'
      ? css`
          background: #fee2e2;
          color: #b91c1c;
          &:hover {
            background: #fecaca;
          }
        `
      : css`
          background: #2563eb;
          color: #fff;
          &:hover {
            background: #1d4ed8;
          }
          &:disabled {
            background: #e5e7eb;
            color: #9ca3af;
            cursor: not-allowed;
          }
        `}
`;

export const Footer = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #9ca3af;
  text-align: left;
  padding-left: 10px;
`;

export const Loading = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  color: #4b5563;
  border-radius: 12px;
  border-top-left-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 4px;

  span {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: bounce 1.4s infinite ease-in-out;
  }
  span:nth-child(1) {
    animation-delay: -0.32s;
  }
  span:nth-child(2) {
    animation-delay: -0.16s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
`;

// Types
interface Message {
  id: string;
  text: string;
  isUser: boolean;
  citations?: Array<{ snippet: string; source: string }>;
  statusCode?: string;
  statusMessage?: string;
  rawData?: any[]; // For Excel Download
  column_mapping?: { [key: string]: string }; // Friendly Column Names
  uiMode?: string; // For future UI switching
  sqlQuery?: string;
}

interface ChatSize {
  width: number;
  height: number;
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: `### 👋 안녕하세요, 관리자님!

저는 **웨이크업(WakeUp)**의 운영 데이터 분석을 돕는 **AI 어시스턴트**입니다.
복잡한 쿼리 없이, **자연어**로 궁금한 점을 편하게 물어보세요.

---

### 📊 답변 가능한 데이터
*   **상품 운영**: 수집/등록 현황, 성공/실패율
*   **트래킹**: 동기화 상태
*   **통계**: 유저 가입 추이, 작업 처리량

### 💬 이렇게 물어보세요
> * "어제 상품 수집 성공률은 얼마야?"
> * "이번 주 가장 많이 등록된 카테고리는?"
> * "최근 3일간 신규 가입자 수 알려줘."

**데이터 조회 권한에 따라 답변이 제한될 수 있습니다.** 🚀`,
      isUser: false,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHighRiskAllowed, setIsHighRiskAllowed] = useState(false);

  // Size State (Default: 600x720)
  const [size, setSize] = useState<ChatSize>({ width: 600, height: 720 });

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isResizingRef = useRef<'NW' | 'N' | 'W' | null>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width: 0, height: 0 });

  // Load saved size on mount
  useEffect(() => {
    const savedSize = localStorage.getItem('chatWidgetSize');
    if (savedSize) {
      try {
        const parsed = JSON.parse(savedSize);
        if (parsed.width && parsed.height) {
          setSize({
            width: Math.max(400, Math.min(parsed.width, window.innerWidth - 60)), // Boundary check
            height: Math.max(500, Math.min(parsed.height, window.innerHeight - 60)),
          });
        }
      } catch (e) {}
    }
  }, []);

  // Save size on change (Debounced slightly effectively by nature of user interaction, but direct save is fine)
  useEffect(() => {
    localStorage.setItem('chatWidgetSize', JSON.stringify(size));
  }, [size]);

  // Handle Resize
  const handleMouseDown = (e: React.MouseEvent, direction: 'NW' | 'N' | 'W') => {
    e.preventDefault();
    isResizingRef.current = direction;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = { ...size };

    // Add global event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizingRef.current) return;

    const deltaX = startPosRef.current.x - e.clientX; // Left drag increases width
    const deltaY = startPosRef.current.y - e.clientY; // Top drag increases height

    const direction = isResizingRef.current;

    let newWidth = startSizeRef.current.width;
    let newHeight = startSizeRef.current.height;

    // Width adjustment (Left drag) - Only for NW and W
    if (direction === 'NW' || direction === 'W') {
      newWidth = Math.max(400, Math.min(startSizeRef.current.width + deltaX, 1200));
    }

    // Height adjustment (Top drag) - Only for NW and N
    if (direction === 'NW' || direction === 'N') {
      newHeight = Math.max(500, Math.min(startSizeRef.current.height + deltaY, 1200));
    }

    setSize({
      width: newWidth,
      height: newHeight,
    });
  };

  const handleMouseUp = () => {
    isResizingRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // React Query Mutation
  const mutation = useMutation(sendChatPrompt, {
    onSuccess: (data: any) => {
      // API returns camelCase directly from backend
      const {
        responseText,
        sessionId,
        citations,
        statusCode,
        statusMessage,
        rawData,
        column_mapping,
        uiMode,
        sqlQuery,
      } = data;
      if (sessionId) setSessionId(sessionId);

      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText || '답변을 생성하지 못했습니다.',
        isUser: false,
        citations,
        statusCode,
        statusMessage,
        rawData,
        column_mapping,
        uiMode,
        sqlQuery,
      };
      setMessages((prev) => [...prev, botMsg]);
      setSelectedFile(null); // Reset file
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (error: any) => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          text: '죄송합니다. 서버 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          isUser: false,
          statusCode: 'CLIENT_ERROR',
        },
      ]);
    },
  });

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isOpen, mutation.isLoading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Validation: Max 10MB
      if (file.size > 10 * 1024 * 1024) {
        alert('파일 크기는 10MB를 초과할 수 없습니다.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSend = () => {
    if ((!inputValue.trim() && !selectedFile) || mutation.isLoading) return;

    let userText = inputValue;
    if (selectedFile) {
      userText += `\n[첨부파일: ${selectedFile.name}]`;
    }

    setInputValue('');

    // Add User Message
    const userMsg: Message = { id: Date.now().toString(), text: userText, isUser: true };
    setMessages((prev) => [...prev, userMsg]);

    // Trigger Mutation
    // Trigger Mutation
    let userId: number | undefined;
    let name: string | undefined;

    try {
      const storedUser = localStorage.getItem('userInfo');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.id) name = parsed.id; // User ID string (e.g. 'sam')
        if (parsed.userId) userId = Number(parsed.userId); // Numeric ID
      }
    } catch (e) {
      console.error('Failed to parse userInfo', e);
    }

    if (selectedFile) {
      const formData = new FormData();
      formData.append('prompt', inputValue || '파일 분석 요청');
      if (sessionId) formData.append('sessionId', sessionId);
      formData.append('file', selectedFile);
      if (isHighRiskAllowed) formData.append('allowHighRisk', 'true');

      if (userId) formData.append('userId', userId.toString());
      if (name) formData.append('name', name);

      mutation.mutate(formData as any); // formData casting
    } else {
      mutation.mutate({
        prompt: inputValue,
        sessionId,
        allowHighRisk: isHighRiskAllowed,
        userId,
        name,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <LauncherButton $isOpen={isOpen} onClick={() => setIsOpen(true)}>
        <FaCommentDots size={24} />
      </LauncherButton>

      <Wrapper $open={isOpen} $width={size.width} $height={size.height}>
        {/* Resize Handles */}
        <ResizeHandleN onMouseDown={(e) => handleMouseDown(e, 'N')} />
        <ResizeHandleW onMouseDown={(e) => handleMouseDown(e, 'W')} />
        <ResizeHandleNW onMouseDown={(e) => handleMouseDown(e, 'NW')} />

        <CloseButtonArea>
          <CloseButton onClick={() => setIsOpen(false)}>
            <FaTimes />
          </CloseButton>
        </CloseButtonArea>

        <Shell>
          <Header>
            <FaRobot color="#2563eb" />
            <span>WakeUp Guide</span>
          </Header>

          <Messages ref={scrollRef}>
            {messages.map((m) => (
              <Row key={m.id} $alignEnd={m.isUser}>
                <ChatBubble
                  text={m.text}
                  isUser={m.isUser}
                  citations={m.citations}
                  statusCode={m.statusCode}
                  statusMessage={m.statusMessage}
                  rawData={m.rawData}
                  column_mapping={m.column_mapping}
                  uiMode={m.uiMode}
                  sqlQuery={m.sqlQuery}
                />
              </Row>
            ))}

            {mutation.isLoading && (
              <Row>
                <div style={{ marginRight: 8, marginTop: 'auto' }}>
                  <FaRobot color="#9ca3af" size={20} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Loading>
                    <span></span>
                    <span></span>
                    <span></span>
                  </Loading>
                  <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '2px' }}>
                    질문에 따라 DB 조회가 이루어져 최대 5분까지 소요될 수 있습니다.
                  </span>
                </div>
              </Row>
            )}
          </Messages>

          {/* File Preview Chip */}
          {selectedFile && (
            <div
              style={{ padding: '8px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex' }}
            >
              <div
                style={{
                  fontSize: '12px',
                  background: '#e0e7ff',
                  color: '#3730a3',
                  padding: '4px 10px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>📎 {selectedFile.name}</span>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    color: '#3730a3',
                  }}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
          )}

          <Composer>
            <InputGrid>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv,.txt"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  position: 'absolute',
                  left: '10px',
                  bottom: '12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                }}
                title="파일 첨부"
              >
                <FaPaperclip size={16} />
              </button>

              <TextArea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  selectedFile ? '파일에 대해 질문하세요...' : '웨이크업에 대해 궁금한 점을 편하게 물어보세요!'
                }
                disabled={mutation.isLoading}
                style={{ paddingLeft: '40px' }} // Space for attachment button
              />
              <Buttons>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    color: isHighRiskAllowed ? '#ef4444' : '#9ca3af',
                    cursor: 'pointer',
                    marginRight: '8px',
                    userSelect: 'none',
                  }}
                  title="체크 시 실행 시간이 긴 쿼리도 차단하지 않고 강제 실행합니다. (주의 필요)"
                >
                  <input
                    type="checkbox"
                    checked={isHighRiskAllowed}
                    onChange={(e) => setIsHighRiskAllowed(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <FaShieldAlt size={10} />
                  <span>고위험 허용</span>
                </label>
                {/* Cancel button logic could be added here if needed */}
                <Button
                  onClick={handleSend}
                  disabled={mutation.isLoading || (!inputValue.trim() && !selectedFile)}
                  $variant="submit"
                >
                  전송
                </Button>
              </Buttons>
            </InputGrid>

            <Footer>⏎ Enter로 바로 보내기 · Shift + Enter로 줄바꿈</Footer>
          </Composer>
        </Shell>
      </Wrapper>
    </>
  );
}
